import React, { useState, useEffect, forwardRef } from 'react';
import { Form, Button, Row, Col, Stack } from 'react-bootstrap';

const getLocalDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TransactionForm = forwardRef(({ addTransaction, editingTransaction, updateTransaction, cancelEdit }, ref) => {
  const [type, setType] = useState('ingreso');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(getLocalDate());
  const [accrual_date, setAccrualDate] = useState(''); 
  const [category, setCategory] = useState('Servicios');
  const [subcategory, setSubcategory] = useState(''); 
  const [invoice_number, setInvoiceNumber] = useState('');
  const [request_id, setRequestId] = useState('');
  const [cc_commission, setCcCommission] = useState('');
  const [tax_origin, setTaxOrigin] = useState('');
  const [dap_interest, setDapInterest] = useState('');
  const [penalizaciones_booking, setPenalizacionesBooking] = useState('');
  const [notes, setNotes] = useState('');
  const [entity_name, setEntityName] = useState('');
  const [status, setStatus] = useState('pendiente');

  const SUB_CATEGORIES_EXPENSE = [
    "Arriendo", "Cafetería y Snacks", "Combustible", "Comidas de Negocios", 
    "Comisiones Bancarias", "Equipos Tecnológicos", "Estacionamientos", 
    "Eventos Corporativos", "Insumos de Aseo", "Insumos de Oficina", "Internet", 
    "Lavado de Vehículos", "Legales y Notaría", "Mantenimiento Vehículos", 
    "Otros", "Patentes y Permisos", "Peajes y TAG", "Previred", 
    "Redes Sociales", "Servicios Básicos", "Suscripciones de Software", 
    "Telefonía Móvil/Fija", "Viajes de Negocios"
  ];

  const SUB_CATEGORIES_FINANCIAL = [
    "Compra Divisas (USD)", "Devolución de Gasto", "Inversión DAP", "Préstamo / Socios", "Remesa Internacional", "Transferencia Interna"
  ];

  const SUB_CATEGORIES_PAYROLL = [
    "Bonos", "HHEE", "HHEE Domingo", "HHEE Sábado", "Previred", "Sueldo", "Viáticos"
  ];

  const isEditing = !!editingTransaction;

  useEffect(() => {
    if (editingTransaction) {
      setType(editingTransaction.type || 'egreso');
      setAmount(editingTransaction.amount?.toString() || '');
      setDescription(editingTransaction.description || '');
      setDate(editingTransaction.date || getLocalDate());
      setAccrualDate(editingTransaction.accrual_date || ''); 
      setCategory(editingTransaction.category || 'Remuneraciones');
      setSubcategory(editingTransaction.subcategory || ''); 
      setInvoiceNumber(editingTransaction.invoice_number || '');
      setRequestId(editingTransaction.request_id || '');
      setCcCommission(editingTransaction.cc_commission?.toString() || '');
      setTaxOrigin(editingTransaction.tax_origin || '');
      setDapInterest(editingTransaction.dap_interest?.toString() || '');
      setPenalizacionesBooking(editingTransaction.penalizaciones_booking?.toString() || '');
      setNotes(editingTransaction.notes || '');
      setEntityName(editingTransaction.entity_name || '');
      setStatus(editingTransaction.status || 'pendiente');
    } else {
      resetForm();
    }
  }, [editingTransaction]);

  const resetForm = () => {
    setType('ingreso');
    setAmount('');
    setDescription('');
    setDate(getLocalDate());
    setAccrualDate(''); 
    setCategory('Servicios');
    setSubcategory(''); 
    setInvoiceNumber('');
    setRequestId('');
    setCcCommission('');
    setTaxOrigin('');
    setDapInterest('');
    setPenalizacionesBooking('');
    setNotes('');
    setEntityName('');
    setStatus('pendiente');
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const safeDate = date || getLocalDate();
    const safeAccrualDate = accrual_date || null;
    
    const needsSubcat = ['Gasto', 'Movimiento Financiero', 'Remuneraciones'].includes(category);

    const transactionData = {
      type,
      amount: parseFloat(amount),
      description,
      date: safeDate,
      accrual_date: safeAccrualDate, 
      category,
      subcategory: needsSubcat ? subcategory : null, 
      invoice_number,
      request_id,
      cc_commission: parseFloat(cc_commission) || 0,
      tax_origin,
      dap_interest: parseFloat(dap_interest) || 0,
      penalizaciones_booking: parseFloat(penalizaciones_booking) || 0,
      notes,
      entity_name,
      status: isEditing ? status : 'pendiente',
    };

    if (isEditing && editingTransaction.id) {
      updateTransaction({ ...transactionData, id: editingTransaction.id });
    } else {
      addTransaction(transactionData);
    }
    resetForm();
  };

  return (
    <Form ref={ref} onSubmit={handleSubmit} className="p-3 border rounded shadow-sm bg-white">
      <h3 className="mb-3">{isEditing ? '✏️ Editar' : '➕ Agregar'} Transacción</h3>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="transactionType">
            <Form.Label className="fw-bold">Tipo <span className="text-danger">*</span></Form.Label>
            <Form.Select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="egreso">Egreso</option>
              <option value="ingreso">Ingreso</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="transactionAmount">
            <Form.Label className="fw-bold">Monto <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ej: 50000"
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group controlId="transactionDescription">
            <Form.Label className="fw-bold">Descripción</Form.Label>
            <Form.Control
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Pago de arriendo"
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Group controlId="transactionEntityName">
            <Form.Label className="fw-bold">Nombre de Empresa / Conductor <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={entity_name}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="Ej: Taxi Express S.A. / Juan Pérez"
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={isEditing ? 4 : 6}>
          <Form.Group controlId="transactionDate">
            <Form.Label className="fw-bold">Fecha <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </Form.Group>
        </Col>
        <Col md={isEditing ? 4 : 6}>
          <Form.Group controlId="transactionAccrualDate">
            <Form.Label className="fw-bold">Fecha de Imputación</Form.Label>
            <Form.Control
              type="date"
              value={accrual_date}
              onChange={(e) => setAccrualDate(e.target.value)}
              title="Fecha real de imputación del gasto (para EERR)"
            />
          </Form.Group>
        </Col>
        {isEditing && (
          <Col md={4}>
            <Form.Group controlId="transactionStatus">
              <Form.Label className="fw-bold">Estado</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="pendiente">Pendiente</option>
                <option value="Por Procesar">Por Procesar</option>
                <option value="Esperando OC/HES">Esperando OC/HES</option>
                <option value="incobrable">Incobrable</option>
                <option value="completado">Completado</option>
              </Form.Select>
            </Form.Group>
          </Col>
        )}
      </Row>
      <Row className="mb-3">
        <Col md={['Gasto', 'Movimiento Financiero', 'Remuneraciones'].includes(category) ? 4 : 6}>
          <Form.Group controlId="transactionCategory">
            <Form.Label className="fw-bold">Categoría <span className="text-danger">*</span></Form.Label>
            <Form.Select value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(''); }} required>
              <option>Remuneraciones</option>
              <option>Gasto</option>
              <option>Movimiento Financiero</option>
              <option>Servicios</option>
              <option>Transporte</option>
              <option>Comida</option>
              <option>Conductores</option>
              <option>Particulares</option>
              <option>Impuestos Directos</option>
              <option>Otros</option>
            </Form.Select>
          </Form.Group>
        </Col>
        {['Gasto', 'Movimiento Financiero', 'Remuneraciones'].includes(category) && (
          <Col md={4}>
            <Form.Group controlId="transactionSubcategory">
              <Form.Label className="fw-bold text-primary">Sub-Categoría</Form.Label>
              <Form.Select value={subcategory} onChange={(e) => setSubcategory(e.target.value)}>
                <option value="">- Seleccionar -</option>
                {category === 'Gasto' && SUB_CATEGORIES_EXPENSE.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                {category === 'Movimiento Financiero' && SUB_CATEGORIES_FINANCIAL.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                {category === 'Remuneraciones' && SUB_CATEGORIES_PAYROLL.map(sub => <option key={sub} value={sub}>{sub}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
        )}
        <Col md={['Gasto', 'Movimiento Financiero', 'Remuneraciones'].includes(category) ? 4 : 6}>
          <Form.Group controlId="transactionInvoiceNumber">
            <Form.Label className="fw-bold">Boleta / Factura</Form.Label>
            <Form.Control
              type="text"
              value={invoice_number}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Ej: F-12345"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="transactionRequestId">
            <Form.Label>Solicitud / Liquidación</Form.Label>
            <Form.Control
              type="text"
              value={request_id}
              onChange={(e) => setRequestId(e.target.value)}
              placeholder="Ej: SOL-987"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="transactionCcCommission">
            <Form.Label>Comision T/C</Form.Label>
            <Form.Control
              type="number"
              value={cc_commission}
              onChange={(e) => setCcCommission(e.target.value)}
              placeholder="Ej: 9500"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="transactionTaxOrigin">
            <Form.Label>Origen del Impuesto</Form.Label>
            <Form.Control
              type="text"
              value={tax_origin}
              onChange={(e) => setTaxOrigin(e.target.value)}
              placeholder="Ej: IVA, Retención"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="transactionDapInterest">
            <Form.Label>Intereses DAP</Form.Label>
            <Form.Control
              type="number"
              value={dap_interest}
              onChange={(e) => setDapInterest(e.target.value)}
              placeholder="Ej: 1500"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="transactionPenalizacionesBooking">
            <Form.Label>Penalizaciones Booking</Form.Label>
            <Form.Control
              type="number"
              value={penalizaciones_booking}
              onChange={(e) => setPenalizacionesBooking(e.target.value)}
              placeholder="Ej: 5000"
            />
          </Form.Group>
        </Col>
      </Row>
       <Row className="mb-3">
        <Col>
          <Form.Group controlId="transactionNotes">
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anotaciones adicionales"
            />
          </Form.Group>
        </Col>
      </Row>
      <Stack direction="horizontal" gap={2} className="justify-content-end">
        {isEditing && <Button variant="secondary" type="button" onClick={cancelEdit}>Cancelar</Button>}
        <Button variant="primary" type="submit">{isEditing ? 'Actualizar' : 'Guardar'} Transacción</Button>
      </Stack>
    </Form>
  );
});

export default TransactionForm;
